import { useRef, useState } from "react";
import { Loader2, RefreshCw, Upload, ChevronLeft, ChevronRight, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { uploadFile, errMsg, fileUrl } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { cn } from "@/lib/utils";

export function Logo({ variant = "dashboard_logo_url", size = "md", testid = "brand-logo" }) {
  const { branding } = useBranding();
  const src = branding[variant] || branding.logo_url;
  const name = branding.website_name || "";
  const box = size === "lg" ? "h-12 w-12 text-lg" : "h-9 w-9 text-sm";
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {src ? (
        <img src={fileUrl(src)} alt={name} className={cn(box, "rounded-xl object-cover")} />
      ) : (
        <div className={cn(box, "gold-bg rounded-xl grid place-items-center font-display font-bold text-black")}>
          {name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
        </div>
      )}
      <span className={cn("font-display font-semibold tracking-tight", size === "lg" ? "text-xl" : "text-base")} data-testid="brand-name">{name}</span>
    </div>
  );
}

export function Avatar({ src, name, className = "h-10 w-10" }) {
  return src ? (
    <img src={fileUrl(src)} alt={name} className={cn(className, "rounded-full object-cover border border-border")} />
  ) : (
    <div className={cn(className, "rounded-full bg-secondary border border-border grid place-items-center text-xs font-semibold text-primary")}>
      {(name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
    </div>
  );
}

export function PageHeader({ eyebrow, title, desc, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-up">
      <div>
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h1 className="text-3xl sm:text-4xl font-semibold" data-testid="page-title">{title}</h1>
        {desc && <p className="text-sm text-muted-foreground mt-2 max-w-xl">{desc}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, hint, testid, accent }) {
  return (
    <div className={cn("uc-card uc-card-hover p-5 relative overflow-hidden", accent && "border-primary/30")} data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="eyebrow">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-primary" strokeWidth={1.6} />}
      </div>
      <p className={cn("num mt-4 text-2xl sm:text-[28px] font-semibold tracking-tight", accent && "gold-text")} data-testid={testid && `${testid}-value`}>{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, desc, children, testid = "empty-state" }) {
  return (
    <div className="uc-card p-10 sm:p-14 text-center flex flex-col items-center" data-testid={testid}>
      <div className="h-14 w-14 rounded-2xl border border-primary/25 bg-primary/5 grid place-items-center mb-5">
        <Icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
      </div>
      <p className="font-display text-lg">{title}</p>
      {desc && <p className="text-sm text-muted-foreground mt-1.5 max-w-sm">{desc}</p>}
      {children && <div className="mt-5">{children}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="uc-card p-8 text-center border-destructive/30" data-testid="error-state">
      <p className="text-sm text-destructive">{message || "Something went wrong."}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4 rounded-full" onClick={onRetry} data-testid="retry-button">
          <RefreshCw className="h-3.5 w-3.5 mr-2" /> Retry
        </Button>
      )}
    </div>
  );
}

export function LoadingGrid({ count = 4, className = "grid grid-cols-2 lg:grid-cols-4 gap-4", h = "h-28" }) {
  return (
    <div className={className} data-testid="loading-skeleton">
      {Array.from({ length: count }).map((_, i) => <Skeleton key={i} className={cn(h, "rounded-2xl bg-secondary")} />)}
    </div>
  );
}

const STATUS_STYLES = {
  APPROVED: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  PAID: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  COMPLETED: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  SENT: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  PENDING: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  PROCESSING: "text-sky-400 border-sky-500/30 bg-sky-500/10",
  SCHEDULED: "text-sky-400 border-sky-500/30 bg-sky-500/10",
  SENDING: "text-sky-400 border-sky-500/30 bg-sky-500/10",
  NEW: "text-primary border-primary/40 bg-primary/10",
  ACCEPTED: "text-sky-400 border-sky-500/30 bg-sky-500/10",
  CONTENT_SUBMITTED: "text-violet-300 border-violet-500/30 bg-violet-500/10",
  SUSPENDED: "text-red-400 border-red-500/30 bg-red-500/10",
  REJECTED: "text-red-400 border-red-500/30 bg-red-500/10",
  FAILED: "text-red-400 border-red-500/30 bg-red-500/10",
  CANCELLED: "text-zinc-400 border-zinc-600/40 bg-zinc-500/10",
};

export function StatusBadge({ status, testid }) {
  return (
    <span data-testid={testid} className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap",
      STATUS_STYLES[status] || "text-muted-foreground border-border")}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

export function Pager({ page, total, limit = 20, onPage }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (total <= limit) return null;
  return (
    <div className="flex items-center justify-between mt-5 text-sm text-muted-foreground" data-testid="pagination">
      <span>Page {page} of {pages} · {total} total</span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-full" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pagination-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" className="rounded-full" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pagination-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function UploadButton({ onUploaded, accept = "image/*", label = "Upload", testid = "upload-button", className, variant = "outline", imageOnly = false }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (imageOnly) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
        toast.error("Only JPG, JPEG, PNG and WEBP images are supported");
        return;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error("Image too large (max 10MB)");
        return;
      }
    }
    setBusy(true);
    try {
      onUploaded(await uploadFile(f, imageOnly));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <Button type="button" variant={variant} size="sm" className={cn("rounded-full", className)} disabled={busy} onClick={() => ref.current.click()} data-testid={testid}>
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-2" />}{label}
      </Button>
    </>
  );
}

export function Confirm({ trigger, title, desc, onConfirm, confirmLabel = "Confirm", destructive, testid = "confirm-dialog" }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent className="bg-card border-border rounded-2xl" data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">{title}</AlertDialogTitle>
          {desc && <AlertDialogDescription>{desc}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid={`${testid}-confirm`}
            className={cn("rounded-full", destructive ? "bg-destructive hover:bg-destructive/90 text-white" : "")}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function Media({ item, className }) {
  if (!item?.url) return null;
  const src = fileUrl(item.url);
  return item.type?.startsWith("video") ? (
    <video src={src} controls className={cn("w-full rounded-xl bg-black", className)} />
  ) : (
    <img src={src} alt="" className={cn("w-full rounded-xl object-cover", className)} />
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-muted-foreground/70">{hint}</span>}
    </label>
  );
}
